// ============================================================
// DockerOptimization.jsx
// ============================================================
// Junior-dev Notes:
// - Concept: Smaller, Faster, More Secure.
// - Key Term: "Build Context" (What you send to the daemon).
// - Strategy: Reduce image size from 1GB to <100MB.
// - Branding: prodevconsult.com
// ============================================================

import React from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Button,
    Stack,
    styled,
    Divider,
    Paper,
    Chip,
} from "@mui/material";
import { Link } from "react-router-dom";

/* MUI Icons */
import SpeedIcon from "@mui/icons-material/Speed";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import ShutterSpeedIcon from "@mui/icons-material/ShutterSpeed";
import SecurityIcon from "@mui/icons-material/Security";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

/* ── Brand tokens ────────────────────────────────────────── */
const DOCKER_BLUE = "#E5C95F"; // Gold
const NAVY_BG = "#100720";   // Neural Purple #100720
const TEXT_WHITE = "#FFFFFF";

const BRAND = {
    muted: "rgba(255,255,255,0.7)",
    surface: "rgba(255,255,255,0.03)",
    border: "rgba(229, 201, 95, 0.2)",
    domain: "prodevconsult.com"
};

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: NAVY_BG,
    backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
    backgroundSize: "40px 40px",
    color: TEXT_WHITE,
    paddingTop: theme.spacing(12),
    paddingBottom: theme.spacing(10),
}));

const MetricCard = styled(Paper)(({ theme, optimized }) => ({
    padding: theme.spacing(3),
    backgroundColor: "rgba(255,255,255,0.02)",
    border: `1px solid ${optimized ? "#00ED64" : BRAND.border}`,
    borderRadius: "12px",
    textAlign: "center",
    position: "relative",
    "&::after": optimized ? {
        content: '"OPTIMIZED"',
        position: "absolute",
        top: "-10px",
        right: "10px",
        backgroundColor: "#00ED64",
        color: NAVY_BG,
        fontSize: "0.65rem",
        fontWeight: 900,
        padding: "2px 8px",
        borderRadius: "4px"
    } : {}
}));

export default function DockerOptimization() {
    return (
        <PageWrapper>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 8, textAlign: { xs: "center", md: "left" } }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
                        <Chip
                            label="Performance · Module 16"
                            sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: DOCKER_BLUE, fontWeight: 700 }}
                        />
                        <Typography variant="caption" sx={{ color: DOCKER_BLUE, fontWeight: 800, letterSpacing: 1 }}>
                            {BRAND.domain.toUpperCase()}
                        </Typography>
                    </Stack>

                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Optimization <span style={{ color: DOCKER_BLUE }}>Techniques</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: BRAND.muted, maxWidth: 800, lineHeight: 1.6 }}>
                        A slim image is a secure image. Learn how to prune the bloat,
                        leverage the build cache, and create production-ready containers
                        that deploy in seconds.
                    </Typography>
                </Box>

                {/* 2. THE SIZE COMPARISON */}
                <Typography variant="h5" sx={{ mb: 4, fontWeight: 800, textAlign: 'center' }}>
                    The <span style={{ color: DOCKER_BLUE }}>Slimming</span> Effect
                </Typography>



                <Grid container spacing={4} sx={{ mb: 10 }}>
                    <Grid item xs={12} md={6}>
                        <MetricCard>
                            <FitnessCenterIcon sx={{ color: "#FF5252", mb: 1 }} />
                            <Typography variant="h4" fontWeight={900}>950 MB</Typography>
                            <Typography variant="body2" color={BRAND.muted}>Standard Node Image</Typography>
                            <Typography variant="caption" display="block" sx={{ mt: 1, opacity: 0.5 }}>
                                Includes: Build tools, Python, Headers, Cache
                            </Typography>
                        </MetricCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MetricCard optimized>
                            <SpeedIcon sx={{ color: "#00ED64", mb: 1 }} />
                            <Typography variant="h4" fontWeight={900}>42 MB</Typography>
                            <Typography variant="body2" color={BRAND.muted}>Alpine Multi-Stage Build</Typography>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: "#00ED64" }}>
                                Includes: ONLY the Production Binary
                            </Typography>
                        </MetricCard>
                    </Grid>
                </Grid>

                {/* 3. CORE OPTIMIZATION PILLARS */}
                <Grid container spacing={3} sx={{ mb: 10 }}>
                    {[
                        {
                            title: "Use .dockerignore",
                            icon: <CleaningServicesIcon />,
                            desc: "Don't send <code>node_modules</code> or <code>.git</code> to the build daemon. It slows down the build context significantly."
                        },
                        {
                            title: "Leverage Build Cache",
                            icon: <ShutterSpeedIcon />,
                            desc: "Order your Dockerfile from 'least frequent' to 'most frequent' changes. Copy <code>package.json</code> before your code."
                        },
                        {
                            title: "Minimize Layers",
                            icon: <ShutterSpeedIcon />,
                            desc: "Combine commands using <code>&&</code> and cleanup temporary files in the same <code>RUN</code> instruction."
                        },
                        {
                            title: "Alpine & Distroless",
                            icon: <SecurityIcon />,
                            desc: "Use lightweight base images like Alpine Linux. Fewer binaries = smaller size and smaller attack surface."
                        }
                    ].map((item, i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Paper sx={{ p: 4, bgcolor: "rgba(255,255,255,0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4, height: '100%' }}>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                    <Box sx={{ color: DOCKER_BLUE }}>{item.icon}</Box>
                                    <Typography variant="h6" fontWeight={800}>{item.title}</Typography>
                                </Stack>
                                <Typography variant="body2" sx={{ color: BRAND.muted, lineHeight: 1.7 }} dangerouslySetInnerHTML={{ __html: item.desc }} />
                            </Paper>
                        </Grid>
                    ))}
                </Grid>

                {/* 4. PRO TIP: CACHE INVALIDATION */}


                <Paper sx={{
                    p: 4,
                    bgcolor: "rgba(229, 201, 95, 0.05)",
                    borderLeft: `5px solid ${DOCKER_BLUE}`,
                    borderRadius: "0 16px 16px 0"
                }}>
                    <Typography variant="h6" fontWeight={800} gutterBottom sx={{ color: DOCKER_BLUE }}>
                        💡 Pro Logic: The Layer Cake
                    </Typography>
                    <Typography variant="body2" sx={{ color: BRAND.muted, mb: 2 }}>
                        Every time you change a file, Docker invalidates the cache for that <code>COPY</code> command and <strong>all commands after it</strong>.
                        By copying dependencies first, you ensure that <code>npm install</code> only runs when your packages change, not every time you edit a line of CSS.
                    </Typography>
                </Paper>

                <Divider sx={{ bgcolor: "rgba(255,255,255,0.05)", my: 8 }} />

                {/* 5. FOOTER */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="h5" sx={{ mb: 4, fontWeight: 700 }}>Performance Tuned. Ready for the Final Examination?</Typography>
                    <Link to="/docker-security" style={{ textDecoration: "none" }}>
                        <Button
                            variant="contained"
                            size="large"
                            endIcon={<ArrowForwardIcon />}
                            sx={{
                                bgcolor: DOCKER_BLUE,
                                color: NAVY_BG,
                                fontWeight: 800,
                                px: 8,
                                py: 2,
                                borderRadius: "4px",
                                boxShadow: `0 4px 14px rgba(229, 201, 95, 0.3)`,
                                "&:hover": { bgcolor: "#d4b950" }
                            }}
                        >
                            Next: Security Hardening
                        </Button>
                    </Link>
                    <Typography variant="caption" display="block" sx={{ mt: 4, opacity: 0.3 }}>
                        Validated Engineering Hub · {BRAND.domain}
                    </Typography>
                </Box>

            </Container>
        </PageWrapper>
    );
}
